import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAdmin } from "../context/AdminContext";
import { useAstrologers } from "../context/AstrologersContext";
import {
  ArrowLeft,
  LogOut,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Save,
  RefreshCw,
  Image as ImageIcon,
  Settings,
  Loader2,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import SEOPlaybook from "../components/SEOPlaybook";

const FieldInput = ({ label, value, onChange, type = "text", textarea = false }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-700 mb-1 uppercase tracking-wide">
      {label}
    </label>
    {textarea ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#16a3a8]/30 text-sm"
      />
    ) : (
      <input
        type={type}
        value={value === undefined || value === null ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#16a3a8]/30 text-sm"
      />
    )}
  </div>
);

const ListEditor = ({ label, list = [], onChange }) => {
  const update = (i, v) => {
    const next = [...list];
    next[i] = v;
    onChange(next);
  };
  const add = () => onChange([...list, ""]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1 uppercase tracking-wide">
        {label}
      </label>
      <div className="space-y-2">
        {list.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={it}
              onChange={(e) => update(i, e.target.value)}
              className="flex-1 px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#16a3a8]/30 text-sm"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="px-2 text-red-600 hover:bg-red-50 rounded"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="text-xs font-semibold text-[#16a3a8] hover:underline"
        >
          + Add item
        </button>
      </div>
    </div>
  );
};

const AstrologerEditor = ({ astrologer, onSave, onDelete, onMove, isFirst, isLast }) => {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(astrologer);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocal(astrologer);
  }, [astrologer]);

  const set = (key) => (value) => setLocal((prev) => ({ ...prev, [key]: value }));
  const setCsv = (key) => (val) =>
    setLocal((prev) => ({
      ...prev,
      [key]: val.split(",").map((s) => s.trim()).filter(Boolean),
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(local);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 p-4">
        <img src={local.image} alt={local.name} className="w-12 h-12 rounded-full object-cover bg-slate-100" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[#0b1e3f] truncate">{local.name}</div>
          <div className="text-xs text-slate-500 truncate">{local.title}</div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onMove("up")}
            disabled={isFirst}
            className="p-1.5 rounded text-slate-600 hover:bg-slate-100 disabled:opacity-30"
            title="Move up"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={() => onMove("down")}
            disabled={isLast}
            className="p-1.5 rounded text-slate-600 hover:bg-slate-100 disabled:opacity-30"
            title="Move down"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded text-red-600 hover:bg-red-50"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="ml-2 px-3 py-1.5 text-xs font-bold text-[#16a3a8] border border-[#16a3a8] rounded hover:bg-[#16a3a8] hover:text-white"
          >
            {open ? "Close" : "Edit"}
          </button>
        </div>
      </div>

      {open && (
        <div className="p-5 border-t border-slate-200 grid md:grid-cols-2 gap-4 bg-slate-50/60">
          <FieldInput label="Name" value={local.name} onChange={set("name")} />
          <FieldInput label="Title / Subtitle" value={local.title} onChange={set("title")} />
          <FieldInput label="Tagline (e.g. Find Your Truth)" value={local.tagline} onChange={set("tagline")} />
          <FieldInput label="Badge (Editor's Choice / Top Rated)" value={local.badge} onChange={set("badge")} />
          <div className="md:col-span-2 flex items-end gap-3">
            <div className="flex-1">
              <FieldInput label="Avatar Image URL (or upload)" value={local.image} onChange={set("image")} />
            </div>
            <label className="btn-outline-cta px-3 py-2 rounded-md text-xs cursor-pointer flex items-center gap-1">
              <ImageIcon className="w-4 h-4" /> Upload
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => set("image")(reader.result);
                  reader.readAsDataURL(file);
                }}
              />
            </label>
          </div>
          <FieldInput label="Rating (0–10)" type="number" value={local.rating} onChange={(v) => set("rating")(parseFloat(v) || 0)} />
          <FieldInput label="Experience" value={local.experience} onChange={set("experience")} />
          <FieldInput label="Consultations" value={local.consultations} onChange={set("consultations")} />
          <FieldInput label="Price Range" value={local.priceRange} onChange={set("priceRange")} />
          <FieldInput label="Session Modes" value={local.sessionModes} onChange={set("sessionModes")} />

          <div className="md:col-span-2 flex items-center justify-between bg-white border border-slate-200 rounded-md p-3">
            <div>
              <div className="text-xs font-bold text-[#0b1e3f] uppercase tracking-wide">
                Contact Enabled
              </div>
              <p className="text-[12px] text-slate-600 mt-0.5">
                When OFF, the WhatsApp & Instagram buttons appear but won't open chat (dummy).
              </p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={local.contactEnabled !== false}
                onChange={(e) => set("contactEnabled")(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:bg-[#25d366] transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
            </label>
          </div>

          <FieldInput
            label="Expertise (comma separated)"
            value={(local.expertise || []).join(", ")}
            onChange={setCsv("expertise")}
          />
          <FieldInput
            label="Languages (comma separated)"
            value={(local.languages || []).join(", ")}
            onChange={setCsv("languages")}
          />
          <FieldInput label="WhatsApp (with country code)" value={local.whatsapp} onChange={set("whatsapp")} />
          <FieldInput label="Instagram (without @)" value={local.instagram} onChange={set("instagram")} />
          <FieldInput label="Phone" value={local.phone} onChange={set("phone")} />
          <FieldInput label="Email" value={local.email} onChange={set("email")} />

          <div className="md:col-span-2">
            <FieldInput
              label="Description / Bio"
              value={local.description}
              onChange={set("description")}
              textarea
            />
          </div>
          <div className="md:col-span-2 grid md:grid-cols-3 gap-4">
            <ListEditor label="Card Bullets (shown in list)" list={local.bullets || []} onChange={set("bullets")} />
            <ListEditor label="Best For" list={local.bestFor || []} onChange={set("bestFor")} />
            <ListEditor label="Highlights" list={local.highlights || []} onChange={set("highlights")} />
          </div>

          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setLocal(astrologer)}
              className="px-4 py-2 rounded text-sm border border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn-cta px-5 py-2 rounded text-sm flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SiteMetaPanel = () => {
  const { meta, setMeta } = useAstrologers();
  const { toast } = useToast();
  const [local, setLocal] = useState(meta);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocal(meta);
  }, [meta]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setMeta(local);
      toast({ title: "Site content saved" });
    } catch (e) {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 max-w-2xl">
      <div className="flex items-center gap-2">
        <Settings className="w-4 h-4 text-[#16a3a8]" />
        <h2 className="font-bold text-[#0b1e3f]">Site Headlines</h2>
      </div>
      <FieldInput
        label="Hero Title"
        value={local.heroTitle}
        onChange={(v) => setLocal({ ...local, heroTitle: v })}
      />
      <FieldInput
        label="Hero Subtitle"
        value={local.heroSubtitle}
        onChange={(v) => setLocal({ ...local, heroSubtitle: v })}
        textarea
      />
      <FieldInput
        label="Last Updated label"
        value={local.lastUpdated}
        onChange={(v) => setLocal({ ...local, lastUpdated: v })}
      />
      <FieldInput
        label="Tagline (Editor's Choice strip)"
        value={local.tagline}
        onChange={(v) => setLocal({ ...local, tagline: v })}
      />
      <FieldInput
        label="Disclosure label"
        value={local.disclosure}
        onChange={(v) => setLocal({ ...local, disclosure: v })}
      />
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-cta px-5 py-2 rounded text-sm flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { isAdmin, session, logout, verifying } = useAdmin();
  const {
    astrologers,
    updateAstrologer,
    addAstrologer,
    deleteAstrologer,
    reorderAstrologer,
    resetAll,
    loading,
  } = useAstrologers();
  const { toast } = useToast();
  const [tab, setTab] = useState("astrologers");

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#16a3a8]" />
      </div>
    );
  }
  if (!isAdmin) return <Navigate to="/admin/login" replace />;

  const handleSave = async (next) => {
    try {
      await updateAstrologer(next.id, next);
      toast({ title: "Saved", description: `${next.name} updated.` });
    } catch (e) {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    }
  };

  const handleAdd = async () => {
    try {
      await addAstrologer({ name: "New Astrologer" });
      toast({ title: "Astrologer added", description: "Click Edit to update details." });
    } catch (e) {
      toast({ title: "Failed", description: e.message, variant: "destructive" });
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete ${a.name}?`)) return;
    try {
      await deleteAstrologer(a.id);
      toast({ title: "Deleted", description: `${a.name} removed.` });
    } catch (e) {
      toast({ title: "Failed", description: e.message, variant: "destructive" });
    }
  };

  const handleMove = async (id, dir) => {
    try {
      await reorderAstrologer(id, dir);
    } catch (e) {
      toast({ title: "Reorder failed", description: e.message, variant: "destructive" });
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all astrologers and site content to defaults?")) return;
    try {
      await resetAll();
      toast({ title: "Reset complete", description: "All content reset to defaults." });
    } catch (e) {
      toast({ title: "Reset failed", description: e.message, variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-slate-600 hover:text-[#16a3a8] flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Site
            </Link>
            <span className="text-slate-300">|</span>
            <h1 className="text-lg font-extrabold text-[#0b1e3f]">Super Admin</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 hidden sm:inline">{session?.email}</span>
            <button
              onClick={logout}
              className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 flex items-center gap-1"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap gap-2 mb-5">
          {[
            { id: "astrologers", label: "Astrologers" },
            { id: "site", label: "Site Content" },
            { id: "seo", label: "SEO Playbook" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                tab === t.id
                  ? "bg-[#16a3a8] text-white"
                  : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
              }`}
            >
              {t.label}
            </button>
          ))}
          <div className="flex-1" />
          {tab !== "seo" && (
            <button
              onClick={handleReset}
              className="px-3 py-2 rounded-md text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-100 flex items-center gap-1"
            >
              <RefreshCw className="w-4 h-4" /> Reset to defaults
            </button>
          )}
        </div>

        {tab === "astrologers" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Manage the top astrologers list — reorder, edit, add or remove. Click <strong>Edit</strong> then <strong>Save Changes</strong> to publish.
              </p>
              <button
                onClick={handleAdd}
                className="btn-cta px-3 py-2 rounded-md text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add Astrologer
              </button>
            </div>

            {loading && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading...
              </div>
            )}

            <div className="space-y-3">
              {astrologers.map((a, i) => (
                <AstrologerEditor
                  key={a.id}
                  astrologer={a}
                  isFirst={i === 0}
                  isLast={i === astrologers.length - 1}
                  onSave={handleSave}
                  onDelete={() => handleDelete(a)}
                  onMove={(dir) => handleMove(a.id, dir)}
                />
              ))}
            </div>
          </div>
        )}

        {tab === "site" && <SiteMetaPanel />}

        {tab === "seo" && <SEOPlaybook />}
      </div>
    </div>
  );
};

export default AdminDashboard;
